import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import axios from '../utils/axios.utils';

// Tab-local dedupe keyed "<Eastern date>|<route pattern>". Route wrappers remount on Routes re-render;
// the API unique index remains the source of truth across tabs/refreshes.
const recordedVisits = new Set<string>();

interface PageViewTrackerProps {
  // Route pattern (e.g. '/admin/user_management/:userId/edit'), never location.pathname.
  path: string;
}

const PageViewTracker: React.FC<PageViewTrackerProps> = ({ path }) => {
  // Re-check on every navigation, not only when the pattern changes: a tab left open overnight keeps the
  // same pattern, and the next day's visit must still count. The Set keeps it to one post per day.
  const { key: navigationKey } = useLocation();

  useEffect(() => {
    // Impersonation is frontend-only; API still sees the superadmin token. Skip. Stop-impersonate
    // stores the string 'null' instead of removing the key.
    const impersonatedAuthId = sessionStorage.getItem('impersonatedAuthId');
    if (impersonatedAuthId && impersonatedAuthId !== 'null') return;

    // Must match config.time_zone (Eastern): server assigns visited_on via Date.current.
    const visitKey = `${new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' })}|${path}`;
    if (recordedVisits.has(visitKey)) return;

    recordedVisits.add(visitKey);
    axios.post('/page_views', { path }).catch((error) => {
      recordedVisits.delete(visitKey);
      console.warn('Failed to record page view', error);
    });
  }, [path, navigationKey]);

  return null;
};

export default PageViewTracker;
